import { EspacoDeTrabalho, Modulo, moduloDoEndereco, modulosDentroDe } from './modulos';

/*
 * As regras que a moldura usa em mais de um lugar (onda 308).
 *
 * Moravam na `Moldura`, que era a única a desenhar navegação. Com a folha
 * «Mais» do celular (`FolhaDoMais`) são três desenhos — o menu lateral, a
 * barra de baixo e a folha — para as MESMAS perguntas: para onde o item leva,
 * se ele é a tela corrente, quais as iniciais do disco da pessoa. Uma cópia
 * em cada arquivo seria uma oportunidade de a folha e o menu discordarem
 * (a mesma razão do `alcanca` em `modulos.ts`). O registro (`modulos.ts`)
 * não as recebe: ele diz o que existe, e estas dizem como a moldura o mostra.
 */

/** As iniciais do nome, para o disco do cabeçalho e o da folha «Mais». */
export function iniciais(nome: string): string {
  const p = nome.trim().split(/\s+/);
  if (p.length === 1) return p[0].slice(0, 2).toUpperCase();
  return (p[0][0] + p[p.length - 1][0]).toUpperCase();
}

/**
 * Se a tela corrente é deste módulo.
 *
 * Um módulo-porta (o de «Cadastros») está «aqui» também quando a tela
 * corrente é de um dos módulos que ele abre: quem está em Locais de
 * armazenamento chegou por Cadastros, e é o item de Cadastros que tem
 * de acender no menu — não há outro que possa.
 *
 * O filho que só tem rota com `:id` — o Campo da OS, `/os/:id/campo`, que
 * mora `dentroDe` Campo desde a H1 (onda 295) — não se reconhece por prefixo:
 * a recursão acima não o alcança, e em `/os/…/campo` nenhum item do Campo
 * acendia. Por isso o dono do endereço (`moduloDoEndereco`, o mesmo que a
 * trilha e o espaço da tela usam) acende também o módulo dentro do qual ele
 * mora.
 */
export function estaAqui(onde: string, m: Modulo): boolean {
  if (!m.rota) return false;
  const nele = m.rota === '/'
    ? onde === '/' || (!!m.raiz && onde.startsWith(m.raiz))
    : onde === m.rota || (!!m.raiz && onde.startsWith(m.raiz));
  return nele || modulosDentroDe(m.chave).some((filho) => estaAqui(onde, filho))
    || moduloDoEndereco(onde)?.dentroDe === m.chave;
}

/*
 * O endereço para onde o item do menu (ou o atalho da barra, ou o ladrilho da
 * folha) leva.
 *
 * O Painel declara `rota: '/'` desde que era a porta de entrada (23/08). Desde
 * a H1 (onda 295) `/` é a porta do ESPAÇO, e quem tem o Hub entra na lista de
 * OS — o item «Painel» que levasse a `/` levaria à OS. O Painel mora em
 * `/painel`, como já dizem `rotaDoEspaco` e a trilha.
 */
export const enderecoDoModulo = (m: Modulo): string => (m.rota === '/' ? '/painel' : m.rota ?? '/');

/*
 * Se, sem rede, a faixa da rede pode prometer que o lançado sobe depois
 * (Ruling R52, 09/10).
 *
 * Só o Campo lança pela fila de envio: a leitura de instrumento
 * (`guardarEEnviar`, em `api/instrumentos.ts`) fica guardada no aparelho e
 * sobe sozinha quando a rede volta. No LabManager e no Hub nada passa pela
 * fila — a pesagem sem rede tenta de novo e falha —, e uma faixa que dissesse
 * «sobe quando a rede voltar» faria o técnico achar que a pesagem ficou
 * guardada. O espaço é o da tela (`espacoDaTela`); o registro (`modulos.ts`)
 * não muda: ele diz o que existe, e isto diz como a moldura o mostra.
 */
export const faixaPrometeFila = (espaco: EspacoDeTrabalho): boolean => espaco === 'campo';
